import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { AgenciesEngagedData } from "../../types";

interface AgenciesEngagedProps {
  data: AgenciesEngagedData;
  isExport?: boolean;
}

const AgenciesEngaged: React.FC<AgenciesEngagedProps> = ({
  data,
  isExport = false,
}) => {
  const [sortedCategories, setSortedCategories] = useState<
    AgenciesEngagedData["categories"]
  >([]);
  useEffect(() => {
    const sortedData = data.categories.sort(
      (a, b) => b.percentage - a.percentage
    );
    setSortedCategories(sortedData);
  }, []);

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "bar",
      backgroundColor: "transparent",
      height: isExport ? 200 : 400,
    },
    title: {
      text: "",
    },
    exporting: {
      enabled: false,
    },
    xAxis: {
      categories: sortedCategories.map((cat) => cat.name),
      labels: {
        style: {
          color: "#64748B",
          fontSize: isExport ? "8px" : "12px",
          textOverflow: "ellipsis",
        },
      },
      lineColor: "#E2E8F0",
      tickColor: "#E2E8F0",
    },
    yAxis: {
      title: {
        text: "",
      },
      labels: {
        formatter: function () {
          return this.value + "%";
        },
        style: {
          color: "#64748B",
          fontSize: "12px",
          textOverflow: "nowrap",
        },
      },
      gridLineColor: "#F1F5F9",
      max:
        Math.ceil(
          data.categories.reduce(
            (max, cat) => Math.max(max, cat.percentage),
            0
          ) / 5
        ) * 5,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        dataLabels: {
          enabled: false,
        },
      },
    },
    series: [
      {
        type: "bar",
        name: "Percentage",
        data: sortedCategories.map((cat) => ({
          y: cat.percentage,
          color: {
            linearGradient: {
              x1: 0,
              x2: 0,
              y1: 0,
              y2: 1,
            },
            stops: [
              [0, "#8A4B9D"],
              [1, "#592b7b"],
            ],
          },
        })),
      },
    ],
    credits: {
      enabled: false,
    },
    tooltip: {
      formatter: function () {
        return `<b>${this.point.category}</b><br/>Percentage: ${this.y}%`;
      },
    },
  };

  return (
    <Card>
      <CardContent
        className={isExport ? "export-card-content" : ""}
        sx={{ p: isExport ? 0 : 3, pr: isExport ? 0 : 4 }}
      >
        <Typography
          variant={isExport ? "body1" : "h5"}
          component="h2"
          sx={{
            mb: 1,
            fontWeight: 600,
            textAlign: isExport ? "center" : "left",
          }}
        >
          Agencies Engaged Across MDT
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, display: isExport ? "none" : "block" }}
        >
          Multi-disciplinary team collaboration ensures comprehensive support
          for each case.
        </Typography>

        <HighchartsReact highcharts={Highcharts} options={chartOptions} />

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: isExport ? 0 : 2, textAlign: "center" }}
        >
          Breakdown of agency participation in multidisciplinary case
          collaboration
        </Typography>
      </CardContent>
    </Card>
  );
};

export default AgenciesEngaged;
